import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { font, Button, Avatar } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import { getDeals, updateDeal, deleteDeal, getDocTemplates, getTemplateFields, sendTemplate, updateDocTemplate, getPipelines, getMyTeam } from "../api";
import CUSTOM_FIELD_CATALOG from "../data/customFieldCatalog";
import { buyerStages as defaultBuyerStages, sellerStages as defaultSellerStages, stageColors as defaultStageColors } from "../data/mockData";
import ScoreBadge from "../components/ScoreBadge";
import ConfirmModal from "../components/ConfirmModal";
import DealDocumentTimeline from "../components/DealDocumentTimeline";
import useResponsive from "../hooks/useResponsive";

function parseBudget(b) {
  if (!b) return 0;
  const s = b.replace(/[^0-9.kmKM]/g, "");
  const num = parseFloat(s);
  if (isNaN(num)) return 0;
  if (/[mM]/.test(b)) return num * 1000000;
  if (/[kK]/.test(b)) return num * 1000;
  return num;
}

function formatCurrency(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${n}`;
}

function DealCard({ deal, dragDeal, onDragStart, onDragEnd, onDelete, onExpand, expanded, onQuickContract }) {
  const t = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onExpand}
      style={{
        background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 8,
        padding: expanded ? 12 : "8px 10px", marginBottom: 6, cursor: "grab",
        opacity: dragDeal?.id === deal.id ? 0.4 : 1, transition: "all 0.15s",
        overflow: "hidden",
      }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1 }}>
          <Avatar name={deal.client_name || "?"} size={expanded ? 28 : 22} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, lineHeight: 1.3 }}>{deal.client_name || "No Contact"}</span>
              <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: t.emeraldLight, color: t.emerald, fontWeight: 700, fontFamily: font.body }}>{deal.type || "Purchase"}</span>
            </div>
            {/* V1 Story 4.6: show the assigned agent name when the API returns it
                (broker pipeline view). Hidden in personal/solo scope where the field is absent. */}
            {deal.assigned_agent_name && (
              <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.body, marginTop: 1 }}>
                {deal.assigned_agent_name}
              </div>
            )}
            {!expanded && deal.budget && <div style={{ fontSize: 10, fontWeight: 700, color: t.emerald, fontFamily: font.mono }}>{deal.budget}</div>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <div onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }} style={{
              width: 22, height: 22, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", background: menuOpen ? t.surfaceWarm : "transparent", border: `1px solid ${menuOpen ? t.border : "transparent"}`,
              transition: "all 0.15s",
            }}
              onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.background = t.surfaceWarm; }}
              onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = "transparent"; }}
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="3" r="1.5" fill={t.textSecondary} />
                <circle cx="8" cy="8" r="1.5" fill={t.textSecondary} />
                <circle cx="8" cy="13" r="1.5" fill={t.textSecondary} />
              </svg>
            </div>
            {menuOpen && (
              <>
                <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }} />
                <div style={{
                  position: "absolute", top: 26, right: 0, background: t.surface,
                  border: `1.5px solid ${t.border}`, borderRadius: 10, padding: "6px 0",
                  minWidth: 150, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", zIndex: 100,
                  animation: "scaleIn 0.15s ease",
                }}>
                  <div onClick={(e) => { e.stopPropagation(); onDelete(); setMenuOpen(false); }} style={{
                    padding: "9px 16px", display: "flex", alignItems: "center", gap: 10,
                    cursor: "pointer", fontSize: 13, fontFamily: font.body,
                    color: t.coral, transition: "background 0.1s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <span style={{ fontSize: 14 }}>{"\uD83D\uDDD1\uFE0F"}</span>
                    <span>Delete Deal</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 8, borderTop: `1px solid ${t.border}`, paddingTop: 8 }}>
          {deal.property_address && (
            <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 4, lineHeight: 1.3, fontFamily: font.body }}>{deal.property_address}</div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13, color: t.textPrimary, fontFamily: font.mono, fontWeight: 700 }}>{deal.budget || "-"}</div>
            {deal.client_score && <ScoreBadge score={deal.client_score} />}
          </div>
          {deal.client_source && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>Source: {deal.client_source}</div>}
          {deal.timeline && <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>Timeline: {deal.timeline}</div>}
          {/* Quick contract actions */}
          {deal.status === "Offer Made" && onQuickContract && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${t.border}` }}>
              <div
                onClick={(e) => { e.stopPropagation(); onQuickContract(deal, "Counteroffers & Addenda"); }}
                onMouseEnter={e => { e.currentTarget.style.background = `${t.amber}20`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${t.amber}08`; }}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
                  background: `${t.amber}08`, border: `1px solid ${t.amber}25`,
                  borderRadius: 6, cursor: "pointer", transition: "background 0.15s",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#F59E0B", fontFamily: font.body }}>Send Counteroffer</span>
              </div>
            </div>
          )}
          <div style={{ marginTop: 10, borderTop: `1px solid ${t.border}`, paddingTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, marginBottom: 6, fontFamily: font.body }}>Documents & Milestones</div>
            <DealDocumentTimeline dealId={deal.id} compact />
          </div>
        </div>
      )}
    </div>
  );
}

// Dynamic stage → template lookup (replaced hardcoded map)

export default function Pipeline({ workspace, user }) {
  // V2 ADR-003 Phase 4.3 wave 7 — Pipeline reads tokens via useTheme().
  // Two components in this file (DealCard + Pipeline) each call the hook.
  const t = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const [deals, setDeals] = useState([]);
  // V1 Story 4.6: broker-only filter — show every agent's deals on the team,
  // optionally narrowed to one agent.
  const isBroker = !!(workspace && user && (user.team_role === "admin" || user.role === "team_manager"));
  const [teamMembers, setTeamMembers] = useState([]);          // [{ id, name, ... }]
  const [agentFilter, setAgentFilter] = useState("");          // "" = all agents
  const [dragDeal, setDragDeal] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.pipeline || "buyer");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [contractPrompt, setContractPrompt] = useState(null); // { deal, template }
  const [contractFields, setContractFields] = useState(null); // { ready, fields }
  const [fieldValues, setFieldValues] = useState({}); // editable overrides
  const [sendingContract, setSendingContract] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [toast, setToast] = useState(null);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [pipelineData, setPipelineData] = useState(null); // dynamic pipelines from API

  // Load user's pipelines
  useEffect(() => {
    getPipelines().then(data => {
      if (data && data.length > 0) setPipelineData(data);
    }).catch(() => {});
  }, []);

  // Build stages and colors from dynamic pipeline data, with fallback to defaults
  const pipelineTabs = useMemo(() => {
    if (!pipelineData || pipelineData.length === 0) {
      return [
        { key: "buyer", label: "Buyer Pipeline", stages: defaultBuyerStages, stageColors: defaultStageColors },
        { key: "seller", label: "Seller Pipeline", stages: defaultSellerStages, stageColors: defaultStageColors },
      ];
    }
    return pipelineData.map(p => {
      const stageNames = p.stages.map(s => s.name);
      const colors = {};
      p.stages.forEach(s => { colors[s.name] = s.color; });
      return { key: p.slug, id: p.id, label: p.name, stages: stageNames, stageColors: colors };
    });
  }, [pipelineData]);

  const activeTabData = pipelineTabs.find(pt => pt.key === activeTab) || pipelineTabs[0];
  const stages = activeTabData?.stages || [];
  const stageColors = activeTabData?.stageColors || defaultStageColors;

  const fetchDeals = () => {
    const params = {};
    if (workspace) params.workspace = workspace;
    if (agentFilter) params.agent_id = agentFilter;
    return getDeals(params).then(setDeals).catch(console.error).finally(() => setLoading(false));
  };

  // Reload deals when the user, workspace, or agent filter changes.
  useEffect(() => { fetchDeals(); /* eslint-disable-next-line */ }, [workspace, agentFilter]);

  // Brokers viewing a team workspace: load member list to populate the agent dropdown.
  useEffect(() => {
    if (!isBroker || !user?.id) { setTeamMembers([]); return; }
    getMyTeam(user.id)
      .then((data) => setTeamMembers(data?.members || []))
      .catch(() => setTeamMembers([]));
  }, [isBroker, user?.id]);

  // Open contract prompt modal and fetch fields
  const openContractPrompt = async (deal, template) => {
    setContractPrompt({ deal, template });
    setContractFields(null);
    setFieldValues({});
    setLoadingFields(true);
    try {
      const data = await getTemplateFields(template.id, deal.id);
      setContractFields(data);
      // Pre-fill editable fields with current values
      const editable = {};
      data.fields.filter(f => !f.auto && !f.filled).forEach(f => { editable[f.key] = f.value; });
      setFieldValues(editable);
    } catch {
      setContractFields({ ready: false, fields: [] });
    } finally {
      setLoadingFields(false);
    }
  };

  // Quick-send a named template for a deal (e.g. "Counteroffers & Addenda")
  const handleQuickContract = async (deal, templateName) => {
    const pipeline = deal.pipeline_type || activeTab;
    try {
      const templates = await getDocTemplates(pipeline);
      const tmpl = templates.find(tpl => tpl.name === templateName);
      if (tmpl) {
        openContractPrompt(deal, tmpl);
      } else {
        setToast({ message: `Template "${templateName}" not found`, variant: "error" });
      }
    } catch {
      setToast({ message: "Failed to load templates", variant: "error" });
    }
  };

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleDrop = async (stage) => {
    if (dragDeal) {
      const prevStage = dragDeal.status;
      const movedDeal = { ...dragDeal, status: stage };
      setDeals(prev => prev.map(d => d.id === dragDeal.id ? movedDeal : d));
      setDragDeal(null); setDragOver(null);
      try {
        await updateDeal(dragDeal.id, { status: stage });
        // Prompt contract if moved to a stage with a matching auto-send template
        if (stage !== prevStage) {
          const pipeline = movedDeal.pipeline_type || activeTab;
          getDocTemplates(pipeline).then(templates => {
            const tmpl = templates.find(tpl => tpl.stage === stage && tpl.auto_send !== false);
            if (tmpl) openContractPrompt(movedDeal, tmpl);
          }).catch(() => {});
        }
      } catch (err) { console.error(err); }
    }
  };

  const handleSendContract = async () => {
    if (!contractPrompt) return;
    const { deal, template } = contractPrompt;

    // Check if there are still missing agent fields (signer fields are filled during signing)
    if (contractFields) {
      const missing = contractFields.fields.filter(f => !f.auto && !f.filled && f.filled_by !== "signer" && !(fieldValues[f.key]?.trim()));
      if (missing.length > 0) {
        setToast({ message: `Please fill in: ${missing.map(f => f.label).join(", ")}`, variant: "error" });
        return;
      }
    }

    // Determine client email — use override if provided
    const clientEmail = fieldValues.buyer_email || fieldValues.seller_email || deal.client_email;
    const clientName = fieldValues.buyer_name || fieldValues.seller_name || deal.client_name;
    const role = (deal.pipeline_type || "buyer") === "buyer" ? "Buyer" : "Seller";

    if (!clientEmail) {
      setToast({ message: "Client email is required to send documents", variant: "error" });
      return;
    }

    setSendingContract(true);
    try {
      // Build overrides from edited fields
      const overrides = {};
      Object.entries(fieldValues).forEach(([k, v]) => { if (v?.trim()) overrides[k] = v.trim(); });

      await sendTemplate(template.id, {
        deal_id: deal.id,
        client_id: deal.client_id,
        signers: [{ name: clientName, email: clientEmail, role }],
        field_overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });
      setToast({ message: `${template.name} sent for signing`, variant: "success" });
      setContractPrompt(null);
      setContractFields(null);
      fetchDeals();
    } catch (err) {
      setToast({ message: err.message || "Failed to send contract", variant: "error" });
    } finally {
      setSendingContract(false);
    }
  };

  const handleAddField = async (field, filledBy) => {
    if (!contractPrompt) return;
    const { template, deal } = contractPrompt;
    const existing = template.custom_fields || [];
    const updated = [...existing, { key: field.key, label: field.label, type: field.type, filled_by: filledBy }];
    try {
      await updateDocTemplate(template.id, { custom_fields: updated });
      // Update template in memory so subsequent adds see the new field
      contractPrompt.template.custom_fields = updated;
      // Re-fetch fields to refresh the modal
      const data = await getTemplateFields(template.id, deal.id);
      setContractFields(data);
      const editable = {};
      data.fields.filter(f => !f.auto && !f.filled && f.filled_by !== "signer").forEach(f => { editable[f.key] = fieldValues[f.key] || f.value || ""; });
      setFieldValues(editable);
      setAddFieldOpen(false);
    } catch (err) {
      setToast({ message: err.message || "Failed to add field", variant: "error" });
    }
  };

  const handleDeleteDeal = async (id) => {
    try { await deleteDeal(id); fetchDeals(); } catch (err) { console.error(err); }
  };

  const filteredDeals = useMemo(() => {
    let d = deals.filter(deal => {
      // Match by pipeline_id if available, otherwise fall back to pipeline_type/slug
      if (activeTabData?.id && deal.pipeline_id) return deal.pipeline_id === activeTabData.id;
      return (deal.pipeline_type || "buyer") === activeTab;
    });
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(deal =>
        (deal.client_name || "").toLowerCase().includes(q) ||
        (deal.property_address || "").toLowerCase().includes(q) ||
        (deal.type || "").toLowerCase().includes(q)
      );
    }
    return d;
  }, [deals, activeTab, activeTabData, search]);

  // Stats
  const stats = useMemo(() => {
    const totalValue = filteredDeals.reduce((sum, d) => sum + parseBudget(d.budget), 0);
    const closedDeals = filteredDeals.filter(d => d.status === "Closed");
    const closedValue = closedDeals.reduce((sum, d) => sum + parseBudget(d.budget), 0);
    return { total: filteredDeals.length, totalValue, closed: closedDeals.length, closedValue };
  }, [filteredDeals]);

  // Deal types are now in DealTypeSelect component

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading deals...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", display: "flex", flexDirection: "column", minHeight: "calc(100vh - 140px)" }}>
      {/* Pipeline Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `2px solid ${t.border}`, ...(isMobile ? { overflowX: "auto", whiteSpace: "nowrap", flexWrap: "nowrap", WebkitOverflowScrolling: "touch" } : {}) }}>
        {pipelineTabs.map(tab => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setSearch(""); }}
            style={{
              padding: isMobile ? "8px 14px" : "10px 24px", fontSize: 14, fontWeight: activeTab === tab.key ? 700 : 400,
              color: activeTab === tab.key ? t.emerald : t.textSecondary,
              background: "none", border: "none", cursor: "pointer",
              borderBottom: `3px solid ${activeTab === tab.key ? t.emerald : "transparent"}`,
              marginBottom: -2, fontFamily: font.display, transition: "all 0.15s",
              flexShrink: 0,
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary Stats */}
      <div style={isMobile ? { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 16 } : { display: "flex", gap: 12, marginBottom: 16 }}>
        {[
          { label: "Total Deals", value: stats.total, color: t.emerald },
          { label: "Pipeline Value", value: formatCurrency(stats.totalValue), color: t.sky },
          { label: "Closed", value: stats.closed, color: t.emeraldDeep },
          { label: "Closed Value", value: formatCurrency(stats.closedValue), color: t.amber },
        ].map(s => (
          <div key={s.label} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 16px", flex: isMobile ? undefined : 1 }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, letterSpacing: 0.5, marginBottom: 2 }}>{s.label.toUpperCase()}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color, fontFamily: font.mono }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Stage Progress Bar */}
      <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 6, marginBottom: 16, background: t.surfaceWarm }}>
        {stages.map(s => {
          const count = filteredDeals.filter(d => d.status === s).length;
          const pct = filteredDeals.length > 0 ? (count / filteredDeals.length) * 100 : 0;
          return pct > 0 ? (
            <div key={s} title={`${s}: ${count}`} style={{ width: `${pct}%`, background: stageColors[s] || t.emerald, transition: "width 0.3s" }} />
          ) : null;
        })}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 8 : 0, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..."
            style={{ width: isMobile ? "100%" : 200, minWidth: 0, background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8, padding: "7px 12px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }} />
          {/* V1 Story 4.6: broker-only agent filter (shown only on a team workspace for team admins). */}
          {isBroker && teamMembers.length > 0 && (
            <select
              value={agentFilter}
              onChange={e => setAgentFilter(e.target.value)}
              title="Filter pipeline by agent"
              style={{ background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8, padding: "7px 10px", color: t.textPrimary, fontSize: 12, outline: "none", fontFamily: font.body }}
            >
              <option value="">All agents ({teamMembers.length})</option>
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>{m.name || m.email}</option>
              ))}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/deals/new?pipeline=${activeTab}`)}>+ Add Deal</Button>
        </div>
        {!isMobile && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {stages.map(s => {
              const count = filteredDeals.filter(d => d.status === s).length;
              return (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <div style={{ width: 6, height: 6, borderRadius: 2, background: stageColors[s] || t.emerald }} />
                  <span style={{ fontSize: 10, color: t.textSecondary, fontFamily: font.mono }}>{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pipeline Columns — horizontal scroll, stretch to bottom */}
      <div style={{ overflowX: "auto", paddingBottom: 8, flex: 1, display: "flex" }}>
        <div style={{ display: "flex", gap: 10, minWidth: stages.length * (isMobile ? 160 : 200), flex: 1 }}>
          {stages.map(stage => {
            const stageDeals = filteredDeals.filter(d => d.status === stage);
            const isOver = dragOver === stage;
            const stageValue = stageDeals.reduce((sum, d) => sum + parseBudget(d.budget), 0);
            return (
              <div key={stage}
                onDragOver={e => { e.preventDefault(); setDragOver(stage); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => handleDrop(stage)}
                style={{
                  flex: 1, minWidth: isMobile ? 160 : 180, display: "flex", flexDirection: "column",
                  background: isOver ? t.emeraldLight : t.surface,
                  border: `1.5px solid ${isOver ? (stageColors[stage] || t.emerald) + "60" : t.border}`,
                  borderRadius: 10, padding: 10, transition: "background 0.2s, border-color 0.2s",
                }}>
                {/* Column Header */}
                <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `2px solid ${stageColors[stage] || t.emerald}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: stageColors[stage] || t.emerald, letterSpacing: 0.5, fontFamily: font.mono }}>{stage.toUpperCase()}</div>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: (stageColors[stage] || t.emerald) + "15", color: stageColors[stage] || t.emerald, fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{stageDeals.length}</div>
                  </div>
                  {stageValue > 0 && (
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, marginTop: 2 }}>{formatCurrency(stageValue)}</div>
                  )}
                </div>
                {/* Cards */}
                <div style={{ flex: 1, minHeight: 100 }}>
                  {stageDeals.map(deal => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      dragDeal={dragDeal}
                      expanded={expandedId === deal.id}
                      onExpand={() => setExpandedId(expandedId === deal.id ? null : deal.id)}
                      onDragStart={() => setDragDeal(deal)}
                      onDragEnd={() => { setDragDeal(null); setDragOver(null); }}
                      onDelete={() => setConfirmDelete({ id: deal.id, name: deal.property_address || deal.client_name || "this deal" })}
                      onQuickContract={handleQuickContract}
                    />
                  ))}
                  {stageDeals.length === 0 && (
                    <div style={{ padding: "20px 0", textAlign: "center", fontSize: 11, color: t.textMuted, fontFamily: font.body, opacity: 0.5 }}>
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Delete Deal"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => { handleDeleteDeal(confirmDelete.id); setConfirmDelete(null); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.variant === "error" ? t.coral : t.emerald,
          color: "#fff", padding: "12px 20px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          animation: "fadeSlide 0.3s ease",
          ...(isMobile ? { maxWidth: "calc(100vw - 40px)" } : {}),
        }}>
          {toast.message}
        </div>
      )}

      {/* Contract Prompt Modal */}
      {contractPrompt && (() => {
        const allFields = contractFields?.fields || [];
        const agentMissing = allFields.filter(f => !f.auto && !f.filled && f.filled_by !== "signer");
        const signerFields = allFields.filter(f => f.filled_by === "signer");
        const filledFields = allFields.filter(f => f.filled && f.filled_by !== "signer");
        const allReady = contractFields?.ready || (agentMissing.length === 0 && !loadingFields);

        return (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 998,
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "fadeSlide 0.2s ease",
          }} onClick={() => { setContractPrompt(null); setContractFields(null); }}>
            <div style={{
              background: t.surface, borderRadius: 14, width: isMobile ? "calc(100vw - 32px)" : 420, maxWidth: 420, maxHeight: "85vh", overflow: "auto",
              boxShadow: "0 16px 48px rgba(0,0,0,0.3)", border: `1px solid ${t.border}`,
            }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ padding: "18px 22px 14px", borderBottom: `1px solid ${t.border}`, position: "sticky", top: 0, background: t.surface, zIndex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
                  {contractPrompt.template.name}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 3 }}>
                  {loadingFields ? "Checking contract fields..." : allReady ? "All fields ready — send when ready" : `${agentMissing.length} field${agentMissing.length !== 1 ? "s" : ""} need${agentMissing.length === 1 ? "s" : ""} your input`}
                </div>
              </div>

              {loadingFields ? (
                <div style={{ padding: "32px 22px", textAlign: "center", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
                  Loading fields...
                </div>
              ) : (
                <div style={{ padding: "16px 22px" }}>
                  {/* Client card */}
                  <div style={{
                    background: t.surfaceWarm, borderRadius: 8, padding: "10px 12px",
                    border: `1px solid ${t.border}`, marginBottom: 16,
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <Avatar name={contractPrompt.deal.client_name || "?"} size={30} />
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{contractPrompt.deal.client_name || "New Client"}</div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{contractPrompt.deal.client_email || "No email"}</div>
                    </div>
                    {contractPrompt.deal.budget && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: t.emerald, fontFamily: font.mono }}>{contractPrompt.deal.budget}</div>
                    )}
                  </div>

                  {/* Missing agent fields — editable */}
                  {agentMissing.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.coral, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                        REQUIRED FIELDS
                      </div>
                      {agentMissing.map(f => (
                        <div key={f.key} style={{ marginBottom: 10 }}>
                          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: t.textSecondary, marginBottom: 4, fontFamily: font.body }}>
                            {f.label}
                            {f.custom && <span style={{ fontSize: 8, color: t.sky, marginLeft: 6, fontWeight: 700, letterSpacing: 0.3 }}>CRM</span>}
                          </label>
                          <input
                            type={f.type === "date" ? "date" : f.type === "money" ? "number" : "text"}
                            value={fieldValues[f.key] || ""}
                            onChange={e => setFieldValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder={f.type === "money" ? "0" : f.label}
                            style={{
                              width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 12,
                              background: t.surfaceWarm, border: `1.5px solid ${t.coral}30`,
                              borderRadius: 6, color: t.textPrimary, outline: "none", fontFamily: font.body,
                            }}
                            onFocus={e => e.target.style.borderColor = t.emerald}
                            onBlur={e => e.target.style.borderColor = `${t.coral}30`}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Filled fields — read-only summary */}
                  {filledFields.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.emerald, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                        AUTO-FILLED
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
                        {filledFields.map(f => (
                          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 9, color: t.emerald }}>&#10003;</span>
                            <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{f.label}</span>
                            {f.custom && <span style={{ fontSize: 7, color: t.sky, fontWeight: 700 }}>CRM</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signer fields — filled during signing */}
                  {signerFields.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.amber, marginBottom: 8, fontFamily: font.mono, letterSpacing: 0.5 }}>
                        FILLED BY SIGNER
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
                        {signerFields.map(f => (
                          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round"><path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                            <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>{f.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Field */}
                  {(() => {
                    const usedKeys = new Set(allFields.map(f => f.key));
                    const available = CUSTOM_FIELD_CATALOG.filter(f => !usedKeys.has(f.key));
                    if (available.length === 0) return null;
                    return (
                      <div style={{ marginBottom: 12, position: "relative" }}>
                        <div
                          onClick={() => setAddFieldOpen(!addFieldOpen)}
                          style={{
                            display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                            fontSize: 10, fontWeight: 600, color: t.sky, fontFamily: font.body,
                            padding: "6px 0",
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 0.7}
                          onMouseLeave={e => e.currentTarget.style.opacity = 1}
                        >
                          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add CRM Field
                        </div>
                        {addFieldOpen && (
                          <div style={{
                            background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", maxHeight: 200, overflowY: "auto",
                            position: "absolute", left: 0, right: 0, zIndex: 5,
                          }}>
                            {available.map(f => (
                              <div key={f.key} style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "8px 12px", borderBottom: `1px solid ${t.border}`,
                                fontSize: 11, fontFamily: font.body,
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ color: t.textPrimary, fontWeight: 500 }}>{f.label}</span>
                                  <span style={{ fontSize: 8, color: t.textMuted, background: t.surfaceWarm, padding: "1px 4px", borderRadius: 3 }}>{f.type}</span>
                                </div>
                                <div style={{ display: "flex", gap: 4 }}>
                                  <div onClick={() => handleAddField(f, "agent")}
                                    style={{ fontSize: 9, fontWeight: 700, color: t.emerald, cursor: "pointer", padding: "2px 6px", borderRadius: 4, background: `${t.emerald}10`, border: `1px solid ${t.emerald}20` }}
                                    onMouseEnter={e => e.currentTarget.style.background = `${t.emerald}25`}
                                    onMouseLeave={e => e.currentTarget.style.background = `${t.emerald}10`}
                                  >Agent</div>
                                  <div onClick={() => handleAddField(f, "signer")}
                                    style={{ fontSize: 9, fontWeight: 700, color: "#F59E0B", cursor: "pointer", padding: "2px 6px", borderRadius: 4, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "rgba(245,158,11,0.2)"}
                                    onMouseLeave={e => e.currentTarget.style.background = "rgba(245,158,11,0.1)"}
                                  >Signer</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Signing flow */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: `${t.sky}10`, borderRadius: 5, border: `1px solid ${t.sky}20` }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: t.sky, color: "#fff", fontSize: 7, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>1</div>
                      <span style={{ fontSize: 10, color: t.textPrimary, fontFamily: font.body }}>You sign</span>
                    </div>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: `${t.emerald}10`, borderRadius: 5, border: `1px solid ${t.emerald}20` }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: t.emerald, color: "#fff", fontSize: 7, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>2</div>
                      <span style={{ fontSize: 10, color: t.textPrimary, fontFamily: font.body }}>Client signs</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{
                padding: "14px 22px 18px", display: "flex", gap: 10, justifyContent: "flex-end",
                borderTop: `1px solid ${t.border}`, position: "sticky", bottom: 0, background: t.surface,
              }}>
                <Button variant="outline" size="sm" onClick={() => { setContractPrompt(null); setContractFields(null); }}>
                  Skip
                </Button>
                <Button variant="primary" size="sm" onClick={handleSendContract} disabled={sendingContract || loadingFields}>
                  {sendingContract ? "Sending..." : "Send for Signing"}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
